// ── PND.50 (corporate annual) / PND.94 (personal H1) / PND.90-91 (personal annual) ──────────────
// One generic panel per kind: client list → worksheet. All figures are SERVER-computed
// (annualTax.ts) — this file never does tax arithmetic, it only displays what /api/annual-tax
// returns, so the screen, the pot and the payment request cannot disagree.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { useOfficeMailboxes } from '../../utils/officeMailboxes';
import PenaltyCard from './PenaltyCard';
import { FilingDocsCard } from './index';

const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', padding: '16px 18px' };
const inp: React.CSSProperties = { width: 130, padding: '6px 9px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', textAlign: 'right' };
const sel: React.CSSProperties = { padding: '6px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' };
const lbl: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-secondary)' };

type Kind = 'pnd50' | 'pnd94' | 'pnd9091';
const KIND_LABEL: Record<Kind, string> = { pnd50: 'PND.50', pnd94: 'PND.94', pnd9091: 'PND.90/91' };

function Row({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
      <span style={{ ...lbl, width: 250, flexShrink: 0 }}>{label}{hint && <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-tertiary)' }}>{hint}</span>}</span>
      {children}
    </div>
  );
}

function CalcLine({ label, value, strong, negative }: { label: string; value: number; strong?: boolean; negative?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: strong ? 14 : 13, padding: '4px 0', fontWeight: strong ? 800 : 500 }}>
      <span style={{ color: strong ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', color: negative ? '#B42318' : 'var(--text-primary)' }}>{negative ? '−' : ''}{money(value)}</span>
    </div>
  );
}

// ── The worksheet editor ────────────────────────────────────────────────────────────────────────
function Worksheet({ kind, client, year, onBack }: { kind: Kind; client: any; year: number; onBack: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mail, setMail] = useState<any>(null);      // notice preview modal
  const [mailTo, setMailTo] = useState('');
  // Default CC = the accounting firm's office mailbox (Admin → Companies) until the operator types one.
  const officeCc = useOfficeMailboxes().accounting;
  const [mailCcTyped, setMailCc] = useState<string | null>(null);
  const mailCc = mailCcTyped ?? officeCc;
  const [mailSubject, setMailSubject] = useState('');
  const mailBodyRef = useRef<HTMLDivElement | null>(null);   // operator-editable body
  const [sending, setSending] = useState(false);

  const load = () => api.annualTaxWs(kind, client.id, year).then((d: any) => {
    setData(d); setDirty(false);
    const w = d.worksheet || {};
    setForm({
      form_variant: w.form_variant || '', income_main: w.income_main || 0, income_extra: w.income_extra || 0,
      expenses: w.expenses || 0, adj_addback: w.adj_addback || 0, adj_deduct: w.adj_deduct || 0,
      loss_cf: w.loss_cf || 0, rate_mode: w.rate_mode || 'general20', other_rate: w.other_rate || 0,
      allowance_mode: w.allowance_mode || 'auto', allowances: w.allowances || 0, sso_paid: w.sso_paid || 0,
      wht_credit: w.wht_credit || 0, half_paid_mode: w.half_paid_mode || 'auto', half_paid: w.half_paid || 0,
      notes: w.notes || '',
    });
  }).catch(() => setData(null));
  useEffect(() => { setData(null); load(); }, [kind, client.id, year]);   // eslint-disable-line

  const set = (k: string, v: any) => { setForm((f: any) => ({ ...f, [k]: v })); setDirty(true); };
  const numSet = (k: string) => (e: any) => set(k, e.target.value);
  const save = async () => {
    setSaving(true);
    try {
      const b: any = { kind, clientId: client.id, year, ...form };
      const d: any = await api.annualTaxWsSave(b);
      setData(d); setDirty(false);
      const w = d.worksheet || {};
      setForm((f: any) => ({ ...f, half_paid: w.half_paid || 0, allowances: w.allowances || 0 }));
    } catch (e: any) { alert(e.message); } finally { setSaving(false); }
  };
  const preview = async () => {
    try {
      const p: any = await api.annualTaxNoticePreview({ kind, clientId: client.id, year });
      setMail(p); setMailTo(p.to || ''); setMailSubject(p.subject || '');
    } catch (e: any) { alert(e.message); }
  };
  const send = async () => {
    setSending(true);
    try {
      const r: any = await api.annualTaxNoticeSend({ kind, clientId: client.id, year, to: mailTo, cc: mailCc, subject: mailSubject, bodyHtml: mailBodyRef.current?.innerHTML || '' });
      setMail(null); alert(t('pen_sent').replace('{to}', r.sent || mailTo));
    } catch (e: any) { alert(e.message); } finally { setSending(false); }
  };

  const c = data?.calc;
  const auto = data?.auto || {};
  const personal = kind !== 'pnd50';
  const num = (v: any) => Number(v) || 0;

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ border: '1px solid var(--border)', background: 'transparent', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12 }}>
          <i className="ti ti-arrow-left" style={{ marginRight: 5 }} />{t('tx_change_client')}</button>
        <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{client.name}</span>
        {!!client.taxId && <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'ui-monospace, monospace' }}>{client.taxId}</span>}
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 999, padding: '2px 10px' }}>
          {kind === 'pnd9091' && c ? (c.variant === 'pnd90' ? 'PND.90' : 'PND.91') : KIND_LABEL[kind]} · {year}
        </span>
        <div style={{ flex: 1 }} />
        {dirty && <span style={{ fontSize: 11.5, color: '#92400E' }}>{t('atx_unsaved')}</span>}
        <button onClick={save} disabled={saving}
          style={{ padding: '7px 18px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? <i className="ti ti-loader" /> : t('save')}
        </button>
      </div>

      {!data ? <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div> : (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(380px, 1.2fr) minmax(300px, 1fr)', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={card}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>{t('atx_sec_income')}</div>
            {kind === 'pnd9091' && (
              <Row label={t('atx_form_variant')} hint={t('atx_form_variant_hint').replace('{v}', c?.variantSuggested === 'pnd90' ? 'PND.90' : 'PND.91')}>
                <select value={form.form_variant} onChange={e => set('form_variant', e.target.value)} style={sel}>
                  <option value="">{t('atx_variant_auto')} ({c?.variantSuggested === 'pnd90' ? 'PND.90' : 'PND.91'})</option>
                  <option value="pnd91">PND.91</option>
                  <option value="pnd90">PND.90</option>
                </select>
              </Row>
            )}
            <Row label={kind === 'pnd50' ? t('atx_revenue') : kind === 'pnd94' ? t('atx_income_4058') : t('atx_salary')}>
              <input type="number" value={form.income_main} onChange={numSet('income_main')} style={inp} /></Row>
            <Row label={kind === 'pnd50' ? t('atx_other_income') : kind === 'pnd94' ? t('atx_income_extra94') : t('atx_other_income_p')}>
              <input type="number" value={form.income_extra} onChange={numSet('income_extra')} style={inp} /></Row>
            <Row label={kind === 'pnd50' ? t('atx_expenses_corp') : t('atx_expenses_pers')}
              hint={kind === 'pnd9091' ? t('atx_salary_exp_hint').replace('{r}', String(auto.expenseRate ?? 50)).replace('{c}', money(auto.expenseCap ?? 100000)) : kind === 'pnd94' ? t('atx_exp94_hint') : undefined}>
              <input type="number" value={form.expenses} onChange={numSet('expenses')} style={inp} /></Row>
            {kind === 'pnd50' && (<>
              <Row label={t('atx_addback')}><input type="number" value={form.adj_addback} onChange={numSet('adj_addback')} style={inp} /></Row>
              <Row label={t('atx_deduct')}><input type="number" value={form.adj_deduct} onChange={numSet('adj_deduct')} style={inp} /></Row>
              <Row label={t('atx_loss_cf')}><input type="number" value={form.loss_cf} onChange={numSet('loss_cf')} style={inp} /></Row>
            </>)}
          </div>

          {personal && (
            <div style={card}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>{t('atx_sec_allow')}</div>
              {kind === 'pnd9091' ? (<>
                <Row label={t('atx_allowances')} hint={t('atx_allow_auto_hint').replace('{v}', money(auto.allowances))}>
                  <select value={form.allowance_mode} onChange={e => set('allowance_mode', e.target.value)} style={sel}>
                    <option value="auto">{t('atx_mode_auto')} ({money(auto.allowances)})</option>
                    <option value="manual">{t('atx_mode_manual')}</option>
                  </select>
                  {form.allowance_mode === 'manual' && <input type="number" value={form.allowances} onChange={numSet('allowances')} style={inp} />}
                </Row>
                <Row label={t('atx_sso_paid')} hint={t('atx_sso_cap_hint').replace('{c}', money(auto.ssoCap ?? 9000))}>
                  <input type="number" value={form.sso_paid} onChange={numSet('sso_paid')} style={inp} /></Row>
              </>) : (
                <Row label={t('atx_allowances')} hint={t('atx_allow94_hint')}>
                  <input type="number" value={form.allowances} onChange={numSet('allowances')} style={inp} /></Row>
              )}
            </div>
          )}

          {kind === 'pnd50' && (
            <div style={card}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>{t('atx_sec_rate')}</div>
              <Row label={t('atx_rate_mode')}>
                <select value={form.rate_mode} onChange={e => set('rate_mode', e.target.value)} style={sel}>
                  <option value="general20">{t('atx_rate_general')}</option>
                  <option value="sme">{t('atx_rate_sme')}</option>
                  <option value="r15">15%</option>
                  <option value="r10">10%</option>
                  <option value="r5">5%</option>
                  <option value="other">{t('atx_rate_other')}</option>
                </select>
                {form.rate_mode === 'other' && <input type="number" value={form.other_rate} onChange={numSet('other_rate')} style={{ ...inp, width: 80 }} />}
              </Row>
            </div>
          )}

          <div style={card}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>{t('atx_sec_credits')}</div>
            <Row label={t('atx_wht_credit')}><input type="number" value={form.wht_credit} onChange={numSet('wht_credit')} style={inp} /></Row>
            {kind !== 'pnd94' && (
              <Row label={kind === 'pnd50' ? t('atx_half_paid_51') : t('atx_half_paid_94')} hint={t('atx_half_auto_hint').replace('{v}', money(auto.halfPaid))}>
                <select value={form.half_paid_mode} onChange={e => set('half_paid_mode', e.target.value)} style={sel}>
                  <option value="auto">{t('atx_mode_auto')} ({money(auto.halfPaid)})</option>
                  <option value="manual">{t('atx_mode_manual')}</option>
                </select>
                {form.half_paid_mode === 'manual' && <input type="number" value={form.half_paid} onChange={numSet('half_paid')} style={inp} />}
              </Row>
            )}
            <Row label={t('atx_notes')}>
              <input type="text" value={form.notes} onChange={e => set('notes', e.target.value)} style={{ ...inp, width: '100%', textAlign: 'left' }} /></Row>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ ...card, background: '#F8FAFC' }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>{t('atx_sec_calc')}{dirty && <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600, marginLeft: 8 }}>{t('atx_calc_stale')}</span>}</div>
            {c ? (<>
              <CalcLine label={t('atx_c_gross')} value={c.gross} />
              <CalcLine label={t('atx_c_expenses')} value={c.expenses} negative />
              {personal && <CalcLine label={t('atx_c_allowances')} value={c.allowances} negative />}
              {kind === 'pnd9091' && num(c.sso) > 0 && <CalcLine label={t('atx_c_sso')} value={c.sso} negative />}
              <CalcLine label={t('atx_c_net')} value={c.net} />
              <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0' }} />
              <CalcLine label={t('atx_c_statutory')} value={c.statutory} />
              {num(c.whtCredit) > 0 && <CalcLine label={t('atx_c_wht')} value={c.whtCredit} negative />}
              {num(c.halfPaid) > 0 && <CalcLine label={kind === 'pnd50' ? t('atx_c_half51') : t('atx_c_half94')} value={c.halfPaid} negative />}
              {num(data.penalty) > 0 && <CalcLine label={t('pen_title')} value={data.penalty} />}
              <div style={{ borderTop: '1.5px solid var(--border-strong)', margin: '6px 0' }} />
              {num(c.refundable) > 0
                ? <CalcLine label={t('atx_c_refundable')} value={c.refundable} strong />
                : <CalcLine label={t('atx_c_payable')} value={(Number(c.payable) || 0) + (Number(data.penalty) || 0)} strong />}
              {num(c.payable) > 0 && (
                <button onClick={preview} style={{ marginTop: 10, width: '100%', padding: '8px 0', borderRadius: 9, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>
                  <i className="ti ti-mail" style={{ marginRight: 6 }} />{t('atx_notify_btn')}
                </button>
              )}
            </>) : <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('atx_no_ws')}</div>}
          </div>

          <PenaltyCard clientId={client.id} kind={kind} year={year} month={0} />
          <FilingDocsCard clientId={client.id} filing={kind} year={year} month={0} monthName="" />
        </div>
      </div>
      )}

      {mail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !sending && setMail(null)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(680px, 94vw)', maxHeight: '88vh', overflow: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow-md)' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 10 }}>{t('pen_modal_title')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ ...lbl, width: 56 }}>To</span>
              <input value={mailTo} onChange={e => setMailTo(e.target.value)} style={{ ...inp, flex: 1, textAlign: 'left' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ ...lbl, width: 56 }}>Cc</span>
              <input value={mailCc} onChange={e => setMailCc(e.target.value)} style={{ ...inp, flex: 1, textAlign: 'left' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ ...lbl, width: 56 }}>{t('et_subject')}</span>
              <input value={mailSubject} onChange={e => setMailSubject(e.target.value)} style={{ ...inp, flex: 1, textAlign: 'left' }} />
            </div>
            {/* Server-rendered draft the operator may edit in place; what is sent is exactly this box. */}
            <div ref={mailBodyRef} contentEditable suppressContentEditableWarning
              style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, background: '#fff', color: '#111', maxHeight: '48vh', overflowY: 'auto', outline: 'none' }}
              dangerouslySetInnerHTML={{ __html: mail.bodyHtml }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => setMail(null)} disabled={sending} style={{ padding: '7px 16px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
              <button onClick={send} disabled={sending} style={{ padding: '7px 18px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: sending ? 0.6 : 1 }}>
                {sending ? <i className="ti ti-loader" /> : t('pen_send_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Client list for one kind/year ───────────────────────────────────────────────────────────────
export function AnnualPanel({ kind }: { kind: Kind }) {
  const { t } = useTranslation();
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(kind === 'pnd94' ? nowY : nowY - 1);   // annual returns are usually last year's
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState<any>(null);

  const load = () => { setLoading(true); api.annualTaxList(kind, year).then((d: any) => setRows(d.rows || [])).catch(() => setRows([])).finally(() => setLoading(false)); };
  useEffect(() => { setClient(null); load(); }, [kind, year]);   // eslint-disable-line

  if (client) return <Worksheet kind={kind} client={client} year={year} onBack={() => { setClient(null); load(); }} />;

  const list = rows.filter(r => !q || (r.name + ' ' + r.taxId).toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 15, color: 'var(--text-primary)' }}>{KIND_LABEL[kind]}</b>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{kind === 'pnd50' ? t('atx_scope_corp') : t('atx_scope_pers')}</span>
        <div style={{ flex: 1 }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('bk_search_client')}
          style={{ ...inp, width: 220, textAlign: 'left' }} />
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
          {Array.from({ length: 5 }, (_, i) => nowY + 1 - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div> : (
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          {/* The headings in Active Jobs' colour and weight, over its line, at its padding (Calem, report 4bd4ae4e, 2nd
              opening: "Make all menus in the SLF system look like this: the same height, text color, no bold, horizontal
              line colors etc."): black at the regular weight, where they were dark grey in bold, 10px above and below
              where it was 9, over the darker --border-strong line. They keep the rows' 13px, having no size of their own. */}
          <thead><tr style={{ background: 'var(--bg-app)', textAlign: 'left' }}>
            <th style={{ padding: '10px 14px', fontWeight: 400, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-strong)' }}>{t('bk_pick_client')}</th>
            <th style={{ padding: '10px 14px', fontWeight: 400, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-strong)' }}>Tax ID</th>
            {kind === 'pnd9091' && <th style={{ padding: '10px 14px', fontWeight: 400, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-strong)' }}>{t('atx_col_form')}</th>}
            <th style={{ padding: '10px 14px', fontWeight: 400, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-strong)', textAlign: 'right' }}>{t('atx_col_payable')}</th>
            <th style={{ padding: '10px 14px', fontWeight: 400, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-strong)' }}>{t('atx_col_status')}</th>
          </tr></thead>
          <tbody>
            {list.map(r => (
              <tr key={r.id} onClick={() => setClient(r)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover, var(--bg-app))')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <td style={{ padding: '9px 14px', fontWeight: 700, color: 'var(--text-primary)' }}>{r.name}</td>
                <td style={{ padding: '9px 14px', color: 'var(--text-secondary)', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.taxId}</td>
                {kind === 'pnd9091' && <td style={{ padding: '9px 14px', color: 'var(--text-secondary)', fontSize: 12 }}>{r.hasWorksheet ? (r.variant === 'pnd90' ? 'PND.90' : 'PND.91') : ''}</td>}
                <td style={{ padding: '9px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: r.payable > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                  {r.hasWorksheet ? (r.refundable > 0 ? `(${money(r.refundable)})` : money(r.payable + (r.penalty || 0))) : '—'}
                </td>
                <td style={{ padding: '9px 14px' }}>
                  {r.filed ? <span style={{ fontSize: 11, fontWeight: 800, color: '#166534', background: '#E8F6EE', borderRadius: 999, padding: '2px 9px' }}>{t('atx_filed')}</span>
                    : r.hasWorksheet ? <span style={{ fontSize: 11, fontWeight: 800, color: '#92400E', background: '#FFFBEB', borderRadius: 999, padding: '2px 9px' }}>{t('atx_in_progress')}</span>
                    : <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>·</span>}
                  {r.penalty > 0 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: '#B42318' }}>+{t('pen_title')}</span>}
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={kind === 'pnd9091' ? 5 : 4} style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12.5 }}>{t('atx_none')}</td></tr>}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// ── End-of-Year tab: Companies (PND.50) | Individuals (PND.90/91) ──────────────────────────────
export function EoYPanel() {
  const { t } = useTranslation();
  const [sub, setSub] = useState<'pnd50' | 'pnd9091'>('pnd50');
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 4, padding: '8px 22px 0', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        {([['pnd50', `${t('atx_sub_companies')} — PND.50`], ['pnd9091', `${t('atx_sub_individuals')} — PND.90/91`]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setSub(k)}
            style={{ padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'transparent', color: sub === k ? 'var(--accent)' : 'var(--text-tertiary)', borderBottom: `2.5px solid ${sub === k ? 'var(--accent)' : 'transparent'}` }}>{label}</button>
        ))}
      </div>
      <AnnualPanel key={sub} kind={sub} />
    </div>
  );
}
